import {
    ApprovalProcess,
    ApprovalScopeEnum,
    ApprovalStatusEnum,
    Company,
    CostCenter,
    Employee,
    ExtendedReferencedUser,
    ExtendedReferencedVendor,
    PurchaseOrderStatusEnum,
} from "@finway-group/shared/lib/models"
import { PURCHASE_ORDER_STATUSES_PRE_APPROVAL, PURCHASE_ORDER_STATUSES_UPDATE_FORBIDDEN } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderStatus.enum"
import { getApprovalProcess } from "@finway-group/shared/lib/utils"
import { TFunction } from "i18next"
import { NO_TAX_RATE } from "Shared/config/consts"
import { AuthzService } from "Shared/services"
import { doAllElementsHaveTheSameFieldValue } from "Shared/utils/helper.utils"
import { ILineItem } from "../LineItems.interfaces"
import { ProcurementTableColumn, PurchaseOrder, PurchaseOrderFormValues, PurchaseOrderTableColumn } from "../PurchaseOrder.interfaces"

export type GenerateColumnsProps<T> = {
    updateRecord: (record: T) => void
    deleteRecord: (key: string) => void
    getRecord: (record: T) => void
    t: TFunction
}

export type GenerateProcurementColumnsProps<T> = {
    updateRecord: (record: T) => void
    t: TFunction
}

export const resolveTax = (li: Array<ILineItem>) => {
    const taxRates = li.map((item) => item.taxRate?._id).filter((id) => id !== undefined)
    const uniqueTaxRates = [...new Set(taxRates)]

    if (uniqueTaxRates.length === 1) {
        return uniqueTaxRates[0]
    }

    return NO_TAX_RATE
}

export const addCostCenterResponsible = ({
    filteredEmployees,
    employees,
    costCenters,
    purchaseOrder,
}: {
    filteredEmployees: Array<Employee>
    employees: Array<Employee>
    costCenters: Array<CostCenter>
    purchaseOrder: PurchaseOrder
}) => {
    const costCenter = costCenters.find((costCenter: CostCenter) => costCenter._id === purchaseOrder.lineItems[0]?.costCenter)
    const isResponsibleUserInTheList = filteredEmployees.find((employee) => employee.id === costCenter?.responsibleUser)

    const responsibleUserHasRightsToApprove = costCenter?.responsibleUser && AuthzService.isUserPurchaseOrderApprover(costCenter?.responsibleUser)

    if (costCenter && !isResponsibleUserInTheList && responsibleUserHasRightsToApprove) {
        const responsibleUser = employees.find((employee) => employee.id === costCenter.responsibleUser)

        if (responsibleUser && !responsibleUser?.activeCompanyProfile.deleted) {
            filteredEmployees.push(responsibleUser)
        }
    }

    return filteredEmployees
}

export const mapPurchaseOrderToTable = (po: PurchaseOrder): PurchaseOrderTableColumn => ({
    key: po._id,
    name: po.purchaseOrderNumber,
    description: po.description,
    requester: `${po.requestedBy.firstName} ${po.requestedBy.lastName}`,
    vendor: po.vendor.name!,
    dateOfIssue: po.dateOfIssue,
    deliveryStatus: po.deliveryStatus,
    invoicedStatus: po.invoicedStatus,
    amount: po.totalGrossPrice,
    status: po.state,
    isService: po.isService,
    requestedBy: po.requestedBy,
    state: po.state,
    currency: po.currency,
    deleted: po.deleted,
    createdBy: po.createdBy,
})

export const mapProcurementToTable = (po: PurchaseOrder): ProcurementTableColumn => ({
    key: po._id,
    name: po.purchaseOrderNumber,
    tags: po.tags,
    totalGrossPrice: po.totalGrossPrice,
    currency: po.currency,
    isService: po.isService,
    purchaseOrderNumber: po.purchaseOrderNumber,
    _id: po._id,
    description: po.description,
    amount: po.totalGrossPrice,
})
export const createDefaultLineItem = (prev?: Partial<PurchaseOrderFormValues["lineItems"][number]>): Omit<Partial<PurchaseOrderFormValues["lineItems"][number]>, "_id"> => ({
    quantity: 1,
    name: "",
    unitOfMeasure: "",
    unitPrice: 0,
    costCenter2: prev?.costCenter2,
    costCenter: prev?.costCenter,
    expenseAccount: prev?.expenseAccount,
    taxRate: {
        _id: prev?.taxRate?._id || NO_TAX_RATE,
    },
    totalTaxPrice: 0,
    totalNetPrice: 0,
    totalGrossPrice: 0,
    materialCode: "",
    link: "",
})

export const isCardMissing = (po: PurchaseOrder) => po.state.currentState === PurchaseOrderStatusEnum.FULFILLMENT && po.createCard && !po.cardId

export const isPurchaseOrderApproverVisible = (po?: Pick<PurchaseOrder, "state">) => !po || PURCHASE_ORDER_STATUSES_PRE_APPROVAL.includes(po.state.currentState)

export const isPurchaseOrderInEditableState = (po?: Pick<PurchaseOrder, "state">) => !po || !PURCHASE_ORDER_STATUSES_UPDATE_FORBIDDEN.includes(po.state.currentState)

export const isPurchaseOrderEditable = (po?: Pick<PurchaseOrder, "state" | "deleted" | "requestedBy" | "createdBy">) =>
    !po || (isPurchaseOrderInEditableState(po) && !po.deleted && AuthzService.isLoggedInUserAllowedToUpdatePurchaseOrder(po))

export const getUniformItemFromLineItems = <T = string>(lineItems: Array<ILineItem>, item: keyof ILineItem) =>
    doAllElementsHaveTheSameFieldValue(lineItems, item) ? (lineItems[0][item] as T) : undefined

const hasOtherDependenciesChanged = (newValues: PurchaseOrderFormValues, previousValues: Partial<PurchaseOrderFormValues>): boolean => {
    const dependencies = ["requestedBy", "vendor", "costCenter", "costCenter2", "expenseAccount"] as const

    return dependencies.some((dep) => {
        const previousValue = previousValues[dep]
        const currentValue = newValues[dep]

        // For objects (requestedBy, vendor), compare _id
        if (dep === "requestedBy" || dep === "vendor") {
            const previousId = (previousValue as ExtendedReferencedUser | ExtendedReferencedVendor)?._id ?? previousValue
            const currentId = (currentValue as ExtendedReferencedUser | ExtendedReferencedVendor)?._id ?? currentValue
            // Use != to handle null/undefined equality
            return previousId !== currentId
        }

        // For strings (costCenter, costCenter2, expenseAccount), compare directly
        // Use !== to handle null/undefined equality
        return previousValue !== currentValue
    })
}

const hasAmountDependenciesChanged = (newValues: PurchaseOrderFormValues, previousValues: Partial<PurchaseOrderFormValues>): boolean => {
    const dependencies = ["totalNetPrice", "totalGrossPrice"] as const

    return dependencies.some((dep) => previousValues[dep] !== newValues[dep])
}

export const shouldRecalculateApprovalProcesses = (
    newValues: PurchaseOrderFormValues,
    previousValues: Partial<PurchaseOrderFormValues> | undefined,
    company: Company,
    approvalProcesses: Array<ApprovalProcess>,
) => {
    if (!previousValues || !newValues) {
        return true // If no existing purchase order, always recalculate
    }

    const poApprovalProcess = getApprovalProcess(ApprovalScopeEnum.PURCHASE_ORDER, approvalProcesses ?? [])

    // If no approval process or not started, always recalculate
    if (!poApprovalProcess || poApprovalProcess?.status === ApprovalStatusEnum.NOT_STARTED) {
        return true
    }

    if (company.recalculateApprovalProcessesInProgress.amount && hasAmountDependenciesChanged(newValues, previousValues)) {
        return true
    }

    if (company.recalculateApprovalProcessesInProgress.other && hasOtherDependenciesChanged(newValues, previousValues)) {
        return true
    }
    // If neither condition is met, don't recalculate

    return false
}
